/**
 * Manifest → graphology graph (doc 147 §9 Phase 2; §8.9.6 revised for theming).
 *
 * Pure: builds a graphology `Graph` from the manifest, using the server's
 * pre-computed layout coordinates when present and falling back to a
 * deterministic circular layout otherwise. Framework-free so it runs
 * identically in tests and in the constellation view.
 *
 * Node colours are baked into the graph attributes (Sigma's WebGL renderer
 * resolves colours at GPU upload — it can't read CSS vars), so the active
 * **mode** has to be known at build time. When the user swaps mode, the
 * caller rebuilds the graph (`useMemo` on `mode`) — see `ConstellationView`.
 *
 * Per the §8.5 madge gate, `graph/` imports only manifest *types* (plus the
 * theme palette tables for colour resolution) — never the parser.
 */
import Graph from 'graphology';
import type { AtlasManifest } from '@atlas-manifest/types';
import { dsPalettes, type Mode, type TokenMap } from '../theme/tokens.js';
import { applyTerritoryToCoords, type TerritoryLayout } from './territoryLayout.js';

export interface LayoutCoords {
  [nodeId: string]: { x: number; y: number };
}

export interface BuildGraphOptions {
  /** Palette mode for node colour resolution; defaults to `'dark'`. */
  mode?: Mode;
  /** Degree at which a node is considered a hub. Default 12. */
  hubThreshold?: number;
  /**
   * Territory layout per domain. When provided, entity coords are translated
   * so each domain's cluster centroid lands on the corresponding territory
   * centroid — see {@link applyTerritoryToCoords}.
   */
  territories?: Record<string, TerritoryLayout>;
}

/**
 * Resolve a `--d-…` domain colour token to its concrete hex value, honouring
 * the active palette (mode). Falls back to `text2` of the same palette when
 * the token is missing or unrecognised.
 */
export function resolveDomainColor(
  colorToken: string | undefined,
  palette: TokenMap = dsPalettes.dark,
): string {
  if (!colorToken) return palette.text2!;
  const key = colorToken.replace(/^--/, '');
  return palette[key] ?? palette.text2!;
}

/**
 * Build the constellation graph. `layout` maps entity id → `{ x, y }`; any
 * entity missing from it gets a deterministic circular position so the graph
 * still renders before/without the server layout.
 */
export function buildGraph(
  manifest: AtlasManifest,
  layout: LayoutCoords = {},
  opts: BuildGraphOptions = {},
): Graph {
  const palette = dsPalettes[opts.mode ?? 'dark'];
  const hubThreshold = opts.hubThreshold ?? 12;
  const graph = new Graph({ multi: false, type: 'undirected' });
  const domainColor = new Map(manifest.domains.map((d) => [d.id, d.color]));
  const n = Math.max(manifest.entities.length, 1);

  // Pre-compute undirected degree from the (deduped) reference list — the
  // graph's own .degree() can't be queried until all edges exist, and we
  // need it on the node attribute payload at insertion time.
  const degreeByEntity = computeDegrees(manifest);

  // When territories are supplied, override the layout arg with translated
  // coords (per-domain cluster centered on its territory centroid).
  const resolvedLayout: LayoutCoords = opts.territories
    ? applyTerritoryToCoords(
        manifest.entities.map((e) => ({
          id: e.id,
          domain: e.domain,
          ...(layout[e.id] ? { x: layout[e.id]!.x, y: layout[e.id]!.y } : {}),
        })),
        opts.territories,
      )
    : layout;

  manifest.entities.forEach((entity, i) => {
    if (graph.hasNode(entity.id)) return;
    const coord = resolvedLayout[entity.id];
    const angle = (2 * Math.PI * i) / n;
    const degree = degreeByEntity.get(entity.id) ?? 0;
    graph.addNode(entity.id, {
      label: entity.name,
      domain: entity.domain,
      x: coord ? coord.x : Math.cos(angle),
      y: coord ? coord.y : Math.sin(angle),
      size: entity.isPersisted ? 6 : 4,
      color: resolveDomainColor(domainColor.get(entity.domain), palette),
      degree,
      isHub: degree >= hubThreshold,
    });
  });

  for (const ref of manifest.references) {
    if (
      ref.from !== ref.to &&
      graph.hasNode(ref.from) &&
      graph.hasNode(ref.to) &&
      !graph.hasEdge(ref.from, ref.to)
    ) {
      graph.addEdge(ref.from, ref.to, { kind: ref.kind, fieldName: ref.fromField });
    }
  }

  return graph;
}

/** Undirected degree per entity, ignoring self-loops and duplicate pairs. */
function computeDegrees(manifest: AtlasManifest): Map<string, number> {
  const degrees = new Map<string, number>();
  const seen = new Set<string>();
  for (const ref of manifest.references) {
    if (ref.from === ref.to) continue;
    const key = ref.from < ref.to ? `${ref.from}|${ref.to}` : `${ref.to}|${ref.from}`;
    if (seen.has(key)) continue;
    seen.add(key);
    degrees.set(ref.from, (degrees.get(ref.from) ?? 0) + 1);
    degrees.set(ref.to, (degrees.get(ref.to) ?? 0) + 1);
  }
  return degrees;
}
